import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, FileText, Lock, Plus, Star, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, Caption, Spinner, TierBadge } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { api, asList, enc, errMsg, openApiPath } from "@/lib/api";
import { cn } from "@/lib/utils";

const LIMITS = { free: { resume: 0, cover_letter: 0 }, pro: { resume: 1, cover_letter: 1 }, elite: { resume: 3, cover_letter: 3 } };
const KINDS = [["resume", "Resumes"], ["cover_letter", "Cover letters"]];
const OK_TYPES = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];

const DocRow = ({ d, onDefault, onDelete }) => (
  <div className="flex items-center gap-3 rounded-lg border border-[#27272A] bg-[#09090B] p-3" data-testid={`doc-${d.doc_id}`}>
    <FileText className="h-4 w-4 shrink-0 text-[#D4AF37]" />
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm text-zinc-200">{d.label || d.name}</div>
      <div className="font-mono text-[10px] uppercase text-zinc-600">{d.uploaded_at && new Date(d.uploaded_at).toLocaleDateString()}{d.is_default && " · default"}</div>
    </div>
    <button onClick={() => onDefault(d)} title="Make default" data-testid={`doc-default-${d.doc_id}`} className={cn("transition-colors", d.is_default ? "text-[#D4AF37]" : "text-zinc-600 hover:text-[#D4AF37]")}><Star className="h-4 w-4" fill={d.is_default ? "currentColor" : "none"} /></button>
    <button onClick={() => openApiPath(() => api.post(`/profile/documents/${enc(d.doc_id)}/link`)).catch((e) => toast.error(errMsg(e)))} title="View" data-testid={`doc-view-${d.doc_id}`} className="text-zinc-500 hover:text-zinc-200"><ExternalLink className="h-4 w-4" /></button>
    <button onClick={() => onDelete(d)} title="Delete" data-testid={`doc-delete-${d.doc_id}`} className="text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
  </div>
);

const Slot = ({ kind, label, docs, limit, onUpload, busy }) => {
  const ref = useRef(null);
  const mine = docs.filter((d) => d.kind === kind);
  return (
    <div>
      <div className="mb-2 flex items-center justify-between"><Caption>{label}</Caption><span className="font-mono text-[11px] text-zinc-500" data-testid={`doc-count-${kind}`}>{mine.length}/{limit}</span></div>
      <div className="space-y-2">{mine.map((d) => <DocRow key={d.doc_id} d={d} {...onUpload.rowHandlers} />)}</div>
      <input ref={ref} type="file" accept=".pdf,.docx" className="hidden" onChange={(e) => { onUpload.upload(kind, e.target.files?.[0]); e.target.value = ""; }} data-testid={`doc-input-${kind}`} />
      {mine.length < limit && (
        <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`doc-add-${kind}`}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-[#3F3F46] py-3 text-xs text-zinc-400 transition-colors hover:border-[#D4AF37]/60 hover:text-zinc-200">
          {busy === kind ? <Spinner /> : <Plus className="h-3.5 w-3.5" />}Add {kind === "resume" ? "a resume" : "a cover letter"} · PDF or DOCX
        </button>
      )}
    </div>
  );
};

export const Documents = () => {
  const { tier } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/profile/documents").then((r) => setState({ docs: asList(r.data, "documents"), limits: r.data?.limits })).catch((e) => { const st = e.response?.status; setState(st === 403 ? { docs: [], limits: { resume: 0, cover_letter: 0 } } : { unavailable: st === 404 || st === 405, failed: ![403, 404, 405].includes(st), docs: [] }); });
  useEffect(() => { load(); }, []);

  const limits = state?.limits || LIMITS[tier] || LIMITS.free;
  const upload = async (kind, file) => {
    if (!file) return;
    if (!OK_TYPES.includes(file.type)) return toast.error("PDF or DOCX only");
    if (file.size > 5 * 1024 * 1024) return toast.error("File must be 5MB or smaller");
    setBusy(kind);
    try { const fd = new FormData(); fd.append("file", file); fd.append("kind", kind); fd.append("label", file.name.replace(/\.[^.]+$/, "")); await api.post("/profile/documents", fd); toast.success("Uploaded"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const rowHandlers = {
    onDefault: async (d) => { try { await api.patch(`/profile/documents/${enc(d.doc_id)}`, { is_default: true }); load(); } catch (e) { toast.error(errMsg(e)); } },
    onDelete: async (d) => { if (!window.confirm(`Delete "${d.label || d.name}"?`)) return; try { await api.delete(`/profile/documents/${enc(d.doc_id)}`); load(); } catch (e) { toast.error(errMsg(e)); } },
  };

  return (
    <Card id="documents" className="scroll-mt-24" data-testid="documents-card">
      <div className="mb-1 flex items-center justify-between"><h3 className="font-display text-lg text-zinc-100">Document library</h3><TierBadge tier={tier} /></div>
      <p className="mb-5 text-xs text-zinc-500">Keep tailored versions on file. Elite: 3 resumes + 3 cover letters. Pro: 1 + 1.</p>
      {!state ? <Spinner className="text-[#D4AF37]" /> : state.unavailable ? (
        <p className="rounded-lg border border-[#27272A] bg-[#09090B] p-3 text-xs text-zinc-400" data-testid="documents-unavailable">The document library turns on as soon as the RPOPRO backend update goes live.</p>
      ) : state.failed ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-300" data-testid="documents-error">Couldn't load your documents. <button onClick={load} className="underline" data-testid="documents-retry">Retry</button></div>
      ) : limits.resume + limits.cover_letter === 0 ? (
        <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 text-sm text-zinc-300" data-testid="documents-locked">
          <Lock className="mb-2 h-4 w-4 text-[#D4AF37]" />Store extra resumes and cover letters with Pro or Elite.
          <Link to="/upgrade"><Button size="sm" className="mt-3 w-full" data-testid="documents-upgrade-button">See plans</Button></Link>
        </div>
      ) : (
        <div className="space-y-6">{KINDS.map(([k, l]) => <Slot key={k} kind={k} label={l} docs={state.docs} limit={limits[k] ?? 0} busy={busy} onUpload={{ upload, rowHandlers }} />)}</div>
      )}
    </Card>
  );
};
